import React, { useState, useEffect, useRef } from 'react';
import { 
  IconSearch, IconEdit, IconTrash, IconPlus, 
  IconSortAscending, IconSortDescending,
  IconFileUpload, IconFileDownload, IconCheck, IconX,
  IconEye, IconCardsFilled, IconJson
} from '@tabler/icons-react';
import styles from '../../pages/AdminDashboard.module.css';
import { adminService } from '../../services/adminApi';
import EventCard from '../EventCard';
import EventDetailPanel from '../EventDetailPanel';
import { Event as GlobalEvent } from '@/types/event';

interface Organization {
  id: number;
  name: string;
}

interface Event {
  id: number;
  title: string;
  description: string;
  dateTime: string;
  location: string;
  eventCategory: { id: string; name: string; color: string; code?: string; icon?: string; isDefault?: boolean; displayOrder?: number; } | null;
  maxParticipants: number | null;
  imageUrl: string | null;
  organizationId: number;
  organizationName?: string;
  isActive: boolean;
  createdAt: string;
  subcategory: string | null;
  tags: string;
  updatedAt: string;
  coordinates?: { lat: number; lon: number };
  latitude?: number;
  longitude?: number;
  isInterested?: boolean;
  isSaved?: boolean;
  isCreated?: boolean;
  interests?: any[];
  savedBy?: any[];
  organization?: any;
  _count?: {
    interests: number;
    savedBy: number;
    views: number;
  };
}

// Interface pour un nouvel événement sans ID (qui sera généré côté serveur)
interface NewEvent {
  title: string;
  description: string;
  dateTime: string;
  location: string;
  eventCategory: { id: string; name: string; color: string } | null;
  maxParticipants: number | null;
  imageUrl: string | null;
  organizationId: number;
  organizationName?: string;
  isActive: boolean;
  createdAt: string;
  id?: number; // L'ID est optionnel pour la création
  // Propriétés supplémentaires pour compatibilité
  subcategory?: string | null;
  tags?: string;
  categoryId?: number;
  interests?: any[];
  updatedAt?: string;
  latitude?: number;
  longitude?: number;
}

interface EventManagementProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const EventManagement: React.FC<EventManagementProps> = ({ onSuccess, onError }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<NewEvent | null>(null);
  const [sortField, setSortField] = useState<string>('title');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [importStatus, setImportStatus] = useState<{ success: any[]; errors: any[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewCardOpen, setPreviewCardOpen] = useState(false);
  const [previewPanelOpen, setPreviewPanelOpen] = useState(false);
  const [previewEvent, setPreviewEvent] = useState<Event | null>(null);
  const [categories, setCategories] = useState<any[]>([]);

  const showSuccess = (message: string) => {
    if (onSuccess) onSuccess(message);
  };

  const showError = (message: string) => {
    setError(message);
    if (onError) onError(message);
  };

  // Chargement des données depuis l'API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Récupérer les organisations depuis l'API
        const orgsData = await adminService.getAllOrganizations();
        setOrganizations(orgsData);
        
        // Récupérer les catégories depuis l'API
        const categoriesData = await adminService.getAllEventCategories();
        setCategories(categoriesData);
        
        // Récupérer les événements depuis l'API
        await fetchEvents();
        
      } catch (err: any) {
        console.error('Erreur lors du chargement des données:', err);
        showError('Erreur lors du chargement des données: ' + (err.message || 'Erreur inconnue'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Fonction pour récupérer les événements depuis l'API
  const fetchEvents = async () => {
    try {
      const response = await adminService.getAllEvents();
      
      // Vérifier la structure des données et l'afficher pour le débogage
      console.log('Structure de la réponse API (événements):', response);
      
      // S'assurer que eventsData est un tableau
      let eventsData: any[] = [];
      
      if (Array.isArray(response)) {
        eventsData = response;
      } else if (response && typeof response === 'object') {
        const responseObj = response as Record<string, any>;
        if (responseObj.events && Array.isArray(responseObj.events)) {
          eventsData = responseObj.events;
        } else {
          console.error('Structure de données inattendue:', response);
          eventsData = [];
        }
      } else {
        console.error('Structure de données inattendue:', response);
        eventsData = [];
      }
      
      console.log('Événements après transformation:', eventsData);
      
      // Traiter les données des événements pour s'assurer qu'ils ont tous les champs attendus
      const processedEvents = eventsData.map(event => ({
        ...event,
        // Convertir les champs pour assurer la compatibilité
        id: typeof event.id === 'string' ? parseInt(event.id, 10) : event.id,
        organizationId: typeof event.organizationId === 'string' ? parseInt(event.organizationId, 10) : event.organizationId,
        // Garantir que les champs obligatoires existent
        title: event.title || 'Sans titre',
        description: event.description || 'Aucune description',
        location: event.location || 'Aucun lieu spécifié',
        isActive: typeof event.isActive === 'boolean' ? event.isActive : true,
        // Ajouter le nom de l'organisation si disponible
        organizationName: event.organization ? event.organization.name : 'Organisation inconnue'
      }));
      
      setEvents(processedEvents);
    } catch (error: any) {
      console.error('Erreur lors de la récupération des événements:', error);
      showError(`Erreur lors de la récupération des événements: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const handleAddNew = () => {
    // Créer un nouvel événement avec des valeurs par défaut sans ID
    setCurrentEvent({
      title: '',
      description: '',
      dateTime: new Date().toISOString().slice(0, 16), // Format YYYY-MM-DDTHH:MM
      location: '',
      eventCategory: null,
      maxParticipants: null,
      imageUrl: null,
      organizationId: organizations.length > 0 ? organizations[0].id : 0,
      organizationName: organizations.length > 0 ? organizations[0].name : '',
      isActive: true,
      createdAt: new Date().toISOString()
    });
    setEditModalOpen(true);
  };

  const handleEdit = (event: Event) => {
    // Convertir l'événement existant en NewEvent (compatible avec les deux interfaces)
    const editableEvent: NewEvent = { ...event };
    setCurrentEvent(editableEvent);
    setEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet événement ? Cette action est irréversible.")) {
      try {
        setError(null);
        // Appel à l'API pour supprimer l'événement
        await adminService.deleteEvent(id);
        
        // Mise à jour de l'état local
        setEvents(events.filter(event => event.id !== id));
        showSuccess('Événement supprimé avec succès');
      } catch (err: any) {
        console.error('Error deleting event:', err);
        showError('Erreur lors de la suppression de l\'événement: ' + (err.message || 'Erreur inconnue'));
      }
    }
  };

  const handleSaveEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentEvent) return;

    try {
      setLoading(true);
      setError(null);
      
      // Préparer les données pour l'API
      const eventData = {
        ...currentEvent,
        // Convertir l'ID de la catégorie en objet complet de catégorie
        eventCategory: currentEvent.eventCategory 
          ? { 
              id: currentEvent.eventCategory.id,
              name: currentEvent.eventCategory.name || '',
              color: currentEvent.eventCategory.color || ''
            }
          : null
      };
      
      // Création ou mise à jour selon la présence d'un ID
      if (currentEvent.id) {
        // Mise à jour d'un événement existant
        await adminService.updateEvent(currentEvent.id, eventData);
        showSuccess('Événement mis à jour avec succès');
      } else {
        // Création d'un nouvel événement
        await adminService.createEvent(eventData);
        showSuccess('Événement créé avec succès');
      }

      // Rafraîchir la liste des événements
      await fetchEvents();
      setEditModalOpen(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erreur lors de la sauvegarde de l\'événement';
      console.error(errorMessage, err);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour générer un modèle Excel
  const handleGenerateTemplate = () => {
    try {
      // Utiliser le service d'administration pour générer le modèle
      adminService.generateEventTemplate();
      showSuccess('Modèle d\'importation Excel généré avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du modèle:', error);
      showError('Erreur lors de la génération du modèle Excel.');
    }
  };

  // Fonction pour télécharger le modèle CSV avec des catégories à jour
  const handleDownloadCsvTemplate = async () => {
    try {
      const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/event-categories/template`;
      
      // Afficher un message de chargement
      showSuccess('Téléchargement du modèle en cours...');
      
      // Utiliser fetch plutôt que window.open pour garantir le téléchargement
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      // Obtenir le contenu en tant que blob
      const blob = await response.blob();
      
      // Créer une URL pour le blob
      const url = window.URL.createObjectURL(blob);
      
      // Créer un élément d'ancrage temporaire pour le téléchargement
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'modele-evenements.csv';
      
      // Ajouter au DOM, déclencher le clic, puis nettoyer
      document.body.appendChild(a);
      a.click();
      
      // Nettoyer après le téléchargement
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showSuccess('Modèle CSV téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors du téléchargement du modèle CSV:', error);
      showError('Erreur lors du téléchargement du modèle CSV. Veuillez réessayer.');
    }
  };

  // Fonction pour exporter les événements au format JSON
  const handleExportJson = async () => {
    try {
      // Afficher un message de chargement
      showSuccess('Exportation des événements en JSON en cours...');
      
      // Récupérer les filtres actuels pour l'exportation
      const filters: { category?: string; search?: string; organizationId?: number } = {};
      if (categoryFilter !== 'all') filters.category = categoryFilter;
      if (searchTerm) filters.search = searchTerm;
      if (organizationFilter !== 'all') filters.organizationId = parseInt(organizationFilter);
      
      // Utiliser le service d'administration pour exporter les événements
      const result = await adminService.exportEventsToJson(filters);
      
      if (result.success) {
        showSuccess(`${result.count} événement(s) exporté(s) avec succès en JSON.`);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'exportation JSON:', error);
      showError(`Erreur lors de l'exportation JSON: ${error.message || 'Erreur inconnue'}`);
    }
  };

  // Fonction pour exporter les événements au format CSV
  const handleExportCsv = async () => {
    try {
      // Afficher un message de chargement
      showSuccess('Exportation des événements en CSV en cours...');
      
      // Récupérer les filtres actuels pour l'exportation
      const filters: { category?: string; search?: string; organizationId?: number } = {};
      if (categoryFilter !== 'all') filters.category = categoryFilter;
      if (searchTerm) filters.search = searchTerm;
      if (organizationFilter !== 'all') filters.organizationId = parseInt(organizationFilter);
      
      // Utiliser le service d'administration pour exporter les événements
      const result = await adminService.exportEventsToCsv(filters);
      
      if (result.success) {
        showSuccess(`${result.count} événement(s) exporté(s) avec succès en CSV.`);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'exportation CSV:', error);
      showError(`Erreur lors de l'exportation CSV: ${error.message || 'Erreur inconnue'}`);
    }
  };

  // Fonction pour déclencher le sélecteur de fichiers
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Fonction pour traiter le fichier importé (Excel, CSV ou JSON)
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setError(null);

      // Déterminer le type de fichier
      const fileName = file.name.toLowerCase();
      let result;
      
      // Traiter le fichier selon son type
      if (fileName.endsWith('.csv')) {
        // Traiter comme un CSV
        result = await adminService.processCsvEvents(file);
      } else if (fileName.endsWith('.json')) {
        // Traiter comme un JSON
        result = await adminService.processJsonEvents(file);
      } else {
        // Traiter comme un Excel
        result = await adminService.processExcelEvents(file);
      }
      
      setImportStatus(result);

      // Rafraîchir la liste des événements après l'importation
      if (result.success.length > 0) {
        await fetchEvents();
        showSuccess(`${result.success.length} événement(s) importé(s) avec succès`);
      }
      
      // Afficher un résumé des erreurs s'il y en a
      if (result.errors.length > 0) {
        showError(`${result.errors.length} erreur(s) lors de l'importation. Vérifiez les détails pour plus d'informations.`);
      }
    } catch (err: any) {
      console.error('Erreur lors de l\'importation du fichier:', err);
      showError(`Erreur lors de l'importation: ${err.message || 'Erreur inconnue'}`);
    } finally {
      // Réinitialiser le champ de fichier pour permettre de sélectionner le même fichier à nouveau
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Create category options from categories state
  const categoryOptions = categories.map(category => ({ value: category.id.toString(), label: category.name }));

  // Filter and sort events
  const filteredEvents = events
    .filter(event => 
      (event.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
       event.description.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (categoryFilter === 'all' || (event.eventCategory && event.eventCategory.id === categoryFilter)) &&
      (organizationFilter === 'all' || event.organizationId.toString() === organizationFilter)
    )
    .sort((a, b) => {
      if (sortField === 'title') {
        return sortDirection === 'asc' 
          ? a.title.localeCompare(b.title) 
          : b.title.localeCompare(a.title);
      } else if (sortField === 'dateTime') {
        return sortDirection === 'asc' 
          ? new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime() 
          : new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime();
      } else if (sortField === 'organization') {
        return sortDirection === 'asc' 
          ? (a.organizationName || '').localeCompare(b.organizationName || '') 
          : (b.organizationName || '').localeCompare(a.organizationName || '');
      }
      return 0;
    });

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Dummy functions for preview components
  const handleDummyInterestClick = (eventId: number) => {
    console.log('Intérêt cliqué pour événement (preview):', eventId);
  };

  const handleDummySaveClick = (eventId: number) => {
    console.log('Sauvegarde cliquée pour événement (preview):', eventId);
  };

  const handlePreviewCard = (event: Event) => {
    // Créer une copie de l'événement pour la prévisualisation
    const previewEvent: Event = {
      ...event,
      // S'assurer que eventCategory n'est jamais null pour compatibilité avec le type Event global
      eventCategory: event.eventCategory || {
        id: '5',
        code: 'autre',
        name: 'Autre',
        color: '#607D8B'
      },
      // Ajouter les propriétés manquantes qui sont attendues par les composants d'affichage
      subcategory: event.subcategory || null,
      tags: event.tags || '',
      updatedAt: event.updatedAt || new Date().toISOString(),
      isInterested: false,
      isSaved: false,
      interests: []
    };
    setPreviewEvent(previewEvent);
    setPreviewCardOpen(true);
  };

  const handlePreviewPanel = (event: Event) => {
    // Créer une copie de l'événement pour la prévisualisation
    const previewEvent: Event = {
      ...event,
      // S'assurer que eventCategory n'est jamais null pour compatibilité avec le type Event global
      eventCategory: event.eventCategory || {
        id: '5',
        code: 'autre',
        name: 'Autre',
        color: '#607D8B'
      },
      subcategory: event.subcategory || null,
      tags: event.tags || '',
      updatedAt: event.updatedAt || new Date().toISOString(),
      isInterested: false,
      isSaved: false,
      interests: []
    };
    
    // Convertir les propriétés latitude et longitude en objet coordinates attendu par EventDetailPanel
    previewEvent.coordinates = event.latitude && event.longitude ? {
      lat: event.latitude,
      lon: event.longitude  // Utiliser 'lon' au lieu de 'lng' pour la compatibilité avec le type Coordinates
    } : undefined;
    
    setPreviewEvent(previewEvent);
    setPreviewPanelOpen(true);
  };

  return (
    <div className={styles.adminContainer}>
      <div className={styles.adminHeader}>
        <h2>Gestion des événements</h2>
        <div className={styles.adminActions}>
          <button 
            className={styles.addButton} 
            onClick={handleAddNew}
          >
            <IconPlus size={18} /> Ajouter
          </button>
          <button 
            className={styles.importButton} 
            onClick={handleImportClick}
          >
            <IconFileUpload size={18} /> Importer (Excel/CSV)
          </button>
          <button 
            className={styles.downloadButton} 
            onClick={handleGenerateTemplate}
          >
            <IconFileDownload size={18} /> Modèle Excel
          </button>
          <button 
            className={styles.downloadButton} 
            onClick={handleDownloadCsvTemplate}
          >
            <IconFileDownload size={18} /> Modèle CSV
          </button>
          <button 
            className={styles.downloadButton} 
            onClick={handleExportJson}
          >
            <IconJson size={18} /> Exporter JSON
          </button>
          <button 
            className={styles.downloadButton} 
            onClick={handleExportCsv}
          >
            <IconFileDownload size={18} /> Exporter CSV
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".xlsx,.xls,.csv,.json"
            onChange={handleFileUpload}
          />
        </div>
      </div>

      {/* Affichage des erreurs */}
      {error && (
        <div className={styles.errorAlert}>
          <IconX size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Affichage des résultats d'importation */}
      {importStatus && (
        <div className={styles.importStatus}>
          <div className={styles.importSummary}>
            <h3>Résultats de l'importation</h3>
            <p>
              <span className={styles.successCount}>
                <IconCheck size={16} /> {importStatus.success.length} événements importés avec succès
              </span>
              {importStatus.errors.length > 0 && (
                <span className={styles.errorCount}>
                  <IconX size={16} /> {importStatus.errors.length} erreurs
                </span>
              )}
            </p>
          </div>
          
          {importStatus.errors.length > 0 && (
            <div className={styles.importErrors}>
              <h4>Détails des erreurs</h4>
              <ul>
                {importStatus.errors.map((error, index) => (
                  <li key={index}>
                    <strong>
                      {error.data?.title || error.line ? `Ligne ${error.line}` : 'Données invalides'}
                    </strong>: {error.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Filtre et recherche existants */}
      <div className={styles.filterContainer}>
        <div className={styles.searchBar}>
          <IconSearch size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Rechercher par titre ou description"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <select
          className={styles.select}
          value={categoryFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategoryFilter(e.target.value)}
        >
          <option value="all">Toutes les catégories</option>
          {categoryOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        
        <select
          className={styles.select}
          value={organizationFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOrganizationFilter(e.target.value)}
        >
          <option value="all">Toutes les organisations</option>
          {organizations.map(org => (
            <option key={org.id} value={org.id.toString()}>{org.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Chargement des événements...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <p className={styles.emptyMessage}>Aucun événement trouvé.</p>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th onClick={() => toggleSort('title')}>
                  Titre {sortField === 'title' && (
                    sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                  )}
                </th>
                <th onClick={() => toggleSort('dateTime')}>
                  Date et heure {sortField === 'dateTime' && (
                    sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                  )}
                </th>
                <th onClick={() => toggleSort('location')}>
                  Lieu {sortField === 'location' && (
                    sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                  )}
                </th>
                <th>Catégorie</th>
                <th>Organisation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.length > 0 ? (
                filteredEvents.map((event) => (
                  <tr key={event.id}>
                    <td>{event.title}</td>
                    <td>{new Date(event.dateTime).toLocaleString('fr-FR')}</td>
                    <td>{event.location}</td>
                    <td>
                      {event.eventCategory ? (
                        <span style={{ 
                          display: 'inline-block',
                          backgroundColor: event.eventCategory.color,
                          color: '#fff',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem'
                        }}>
                          {event.eventCategory.name}
                        </span>
                      ) : 'Non catégorisé'}
                    </td>
                    <td>{event.organizationName || 'N/A'}</td>
                    <td className={styles.actions}>
                      <button 
                        className={styles.actionButton}
                        onClick={() => handlePreviewCard(event)}
                        title="Aperçu carte"
                      >
                        <IconCardsFilled size={18} />
                      </button>
                      <button 
                        className={styles.actionButton}
                        onClick={() => handlePreviewPanel(event)}
                        title="Aperçu détaillé"
                      >
                        <IconEye size={18} />
                      </button>
                      <button 
                        className={styles.actionButton}
                        onClick={() => handleEdit(event)}
                        title="Éditer"
                      >
                        <IconEdit size={18} />
                      </button>
                      <button 
                        className={styles.actionButton}
                        onClick={() => handleDelete(event.id)}
                        title="Supprimer"
                      >
                        <IconTrash size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className={styles.noData}>
                    {loading ? 'Chargement des événements...' : 'Aucun événement trouvé.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de prévisualisation de carte d'événement */}
      {previewCardOpen && previewEvent && (
        <div className={styles.modalOverlay} onClick={() => setPreviewCardOpen(false)}>
          <div className={styles.previewContainer} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Aperçu de Carte d'Événement</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setPreviewCardOpen(false)}
              >
                <IconX size={20} />
              </button>
            </div>
            <div className={styles.previewContent}>
              <p className={styles.previewNote}>
                Voici comment l'événement apparaîtra aux utilisateurs dans les listes d'événements
              </p>
              <div className={styles.cardPreview}>
                <EventCard 
                  event={previewEvent as unknown as GlobalEvent}
                  onInterestClick={handleDummyInterestClick}
                  onSaveClick={handleDummySaveClick}
                  onClick={() => {}}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de prévisualisation de panneau détaillé */}
      {previewPanelOpen && previewEvent && (
        <div className={styles.modalOverlay} onClick={() => setPreviewPanelOpen(false)}>
          <div className={styles.previewPanelContainer} onClick={e => e.stopPropagation()}>
            <EventDetailPanel 
              event={previewEvent as unknown as GlobalEvent}
              onClose={() => setPreviewPanelOpen(false)}
              onInterestClick={handleDummyInterestClick}
              onSaveClick={handleDummySaveClick}
            />
          </div>
        </div>
      )}

      {/* Modal for editing/creating event */}
      {editModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {currentEvent?.id ? 'Modifier l\'événement' : 'Ajouter un nouvel événement'}
              </h3>
              <button 
                className={styles.closeButton}
                onClick={() => setEditModalOpen(false)}
              >
                &times;
              </button>
            </div>
            
            {currentEvent && (
              <form onSubmit={handleSaveEvent} className={styles.modalForm}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Titre</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    required
                    value={currentEvent.title}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentEvent({...currentEvent, title: e.target.value})}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Description</label>
                  <textarea
                    className={styles.formTextarea}
                    required
                    value={currentEvent.description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCurrentEvent({...currentEvent, description: e.target.value})}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Date et heure</label>
                    <input
                      type="datetime-local"
                      className={styles.formInput}
                      required
                      value={new Date(currentEvent.dateTime).toISOString().slice(0, 16)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentEvent({
                        ...currentEvent, 
                        dateTime: new Date(e.target.value).toISOString()
                      })}
                    />
                  </div>
                  
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Lieu</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      required
                      value={currentEvent.location}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentEvent({...currentEvent, location: e.target.value})}
                    />
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Catégorie</label>
                    <select
                      className={styles.formSelect}
                      value={currentEvent.eventCategory?.id || ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const categoryId = e.target.value;
                        const selectedCategory = categories.find(cat => cat.id.toString() === categoryId);
                        
                        setCurrentEvent({
                          ...currentEvent,
                          categoryId: categoryId ? parseInt(categoryId) : undefined,
                          eventCategory: selectedCategory ? {
                            id: selectedCategory.id.toString(),
                            name: selectedCategory.name,
                            color: selectedCategory.color || '#6c757d'
                          } : null
                        });
                      }}
                    >
                      <option value="">Sélectionnez une catégorie</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id.toString()}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Nombre maximum de participants</label>
                    <input
                      type="number"
                      min={0}
                      className={styles.formInput}
                      value={currentEvent.maxParticipants || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentEvent({
                        ...currentEvent, 
                        maxParticipants: e.target.value ? Number(e.target.value) : null
                      })}
                    />
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div className={styles.formGroup} style={{ flex: 2 }}>
                    <label className={styles.formLabel}>URL de l'image</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={currentEvent.imageUrl || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentEvent({
                        ...currentEvent, 
                        imageUrl: e.target.value || null
                      })}
                    />
                  </div>
                  
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Organisation</label>
                    <select
                      className={styles.formSelect}
                      value={currentEvent.organizationId.toString()}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCurrentEvent({
                        ...currentEvent, 
                        organizationId: parseInt(e.target.value)
                      })}
                    >
                      <option value="0" disabled>Sélectionnez une organisation</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id.toString()}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <input
                      type="checkbox"
                      checked={currentEvent.isActive}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentEvent({
                        ...currentEvent, 
                        isActive: e.target.checked
                      })}
                    />
                    {' '}Événement actif
                  </label>
                </div>
                
                <div className={styles.modalActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setEditModalOpen(false)}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className={styles.saveButton}
                  >
                    Enregistrer
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
